import { CalendarOff } from 'lucide-react';
import { BreakIcon } from '@/components/timetable/BreakIcon';
import type { Exam } from '@/types/exam';
import type { DeclaredHoliday } from '@/types/timetable';
import { ExamSlotCard } from '@/components/timetable/ExamSlotCard';
import { examsInPeriod, untimedExams } from '@/components/timetable/examPlacement';
import { TimetableSlotCell } from './TimetableSlotCell';
import type {
  DayColumnHeader,
  GridTimeSlot,
  InteractiveTimetableSlot,
  AvailabilityStatus,
} from '../TeacherTimetableSection';

interface TimetableGridProps {
  daysHeader: DayColumnHeader[];
  timeSlots: GridTimeSlot[];
  slotsByDate: Record<string, InteractiveTimetableSlot[]>;
  holidayByDate: Map<string, DeclaredHoliday>;
  /** The week's published papers by date, from `examsByDate`. */
  examsByDate: Map<string, Exam[]>;
  selectedDayISO?: string;
  onSelectDayISO?: (iso: string) => void;
  selectedStatusFilter: 'ALL' | AvailabilityStatus;
  isPeriodCompleted: (dateISO: string, timeEnd: string) => boolean;
  onOpenSlotModal: (slot: InteractiveTimetableSlot, day: DayColumnHeader) => void;
}

export function TimetableGrid({
  daysHeader,
  timeSlots,
  slotsByDate,
  holidayByDate,
  examsByDate,
  selectedDayISO,
  onSelectDayISO,
  selectedStatusFilter,
  isPeriodCompleted,
  onOpenSlotModal,
}: TimetableGridProps) {
  // Determine if a column is selected/active (e.g. Monday by default)
  const activeColumnISO = selectedDayISO || daysHeader[0]?.dateISO;

  return (
    <div className="card bg-surface border border-line rounded-[16px] overflow-hidden shadow-2xs print:border print:rounded-lg print:shadow-none print:overflow-visible print:block">
      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full min-w-[960px] border-collapse text-left">
          <thead>
            <tr className="border-b border-line bg-surface-2/60">
              {/* Top-Left Header: Time / Day */}
              <th className="w-36 p-4 text-center text-sm font-bold text-ink-3 border-r border-line print:w-24 print:p-1.5 print:text-xs">
                Time / Day
              </th>

              {/* Day Column Headers */}
              {daysHeader.map((d) => {
                const isActive = d.dateISO === activeColumnISO;
                const dayExams = examsByDate.get(d.dateISO) ?? [];
                // A paper with no time has no period to sit in, so the day
                // itself carries it — named, not just counted.
                const untimed = untimedExams(dayExams);

                return (
                  <th
                    key={d.dateISO}
                    onClick={() => onSelectDayISO && onSelectDayISO(d.dateISO)}
                    className={`min-w-[140px] p-3 text-center border-r border-line last:border-r-0 cursor-pointer transition-colors print:p-1.5 print:min-w-0 ${
                      isActive
                        ? 'bg-indigo-950/80 text-white dark:bg-indigo-950 border-indigo-500/40'
                        : 'hover:bg-surface-2/80 text-ink'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <span className={`text-sm font-bold print:text-xs ${isActive ? 'text-white' : 'text-ink'}`}>
                        {d.dayName}
                      </span>
                      <span
                        className={`text-[12px] font-semibold print:text-[10px] ${
                          isActive ? 'text-indigo-200' : 'text-ink-3'
                        }`}
                      >
                        {d.dateDisplay}
                      </span>
                      {dayExams.length > 0 && (
                        <span
                          className="mt-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber print:text-[8px]"
                          title={untimed.length > 0 ? untimed.map((exam) => `${exam.subject.name} · ${exam.classSection.displayName}`).join(', ') : undefined}
                        >
                          {dayExams.length === 1 ? 'Exam day' : `${dayExams.length} exams`}
                        </span>
                      )}
                      {untimed.length > 0 && (
                        <span className="text-[10px] font-medium text-ink-3 print:text-[8px]">
                          {untimed.map((exam) => exam.subject.name).join(', ')} · time not set
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-line">
            {timeSlots.map((slot, rowIndex) => {
              const isBreakRow = slot.breakLabel !== null;

              // A break — lunch, recess or assembly — is one band across the
              // week, drawn as the office's and the parent's grids draw it: the
              // time on the left, one cell spanning every day with its name.
              if (isBreakRow) {
                return (
                  <tr key={slot.label} className="bg-surface-2 border-y border-dashed border-line">
                    <td className="px-3 py-2 text-center border-r border-line text-xs font-semibold text-ink-3 whitespace-nowrap print:p-1 print:text-[9px]">
                      {slot.label}
                    </td>
                    <td colSpan={daysHeader.length} className="px-4 py-2 text-center select-none print:p-1">
                      <span className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-ink-2 print:text-[11px] print:gap-1">
                        <BreakIcon
                          label={slot.breakLabel}
                          className="h-3.5 w-3.5 shrink-0 text-amber print:h-3 print:w-3"
                        />
                        {slot.breakLabel}
                      </span>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={slot.label} className="hover:bg-surface-2/20 transition-colors">
                  {/* Time / Period Label Column */}
                  <td className="p-3 text-center border-r border-line bg-surface-2/40 align-middle print:p-1 print:py-1">
                    <div className="flex flex-col items-center justify-center gap-1 print:gap-0.5">
                      <span className="text-sm font-bold text-ink print:text-xs">
                        {/* The number the office gave the period, so "Period 4"
                            here is Period 4 on the admin's grid — counting rows
                            renumbered everything after a break. */}
                        {`Period ${slot.periodNumber}`}
                      </span>
                      <span className="text-[11px] font-medium text-ink-3 whitespace-nowrap print:text-[9px]">
                        {slot.label}
                      </span>
                    </div>
                  </td>

                  {daysHeader.map((d) => {
                      // Check for declared holiday
                      const holiday = holidayByDate.get(d.dateISO);
                      if (holiday) {
                        // One cell per row, never one tall cell: a break row is
                        // a single band across every day, and a cell spanning
                        // down through it pushed the later columns out of line.
                        const isFirstRow = rowIndex === timeSlots.findIndex((row) => row.breakLabel === null);
                        return (
                          <td
                            key={d.dateISO}
                            className="select-none bg-indigo-500/5 p-2 text-center align-middle border-r border-line last:border-r-0 print:p-1"
                          >
                            {isFirstRow ? (
                              <div className="flex flex-col items-center justify-center gap-1 print:gap-0.5">
                                <CalendarOff className="h-4 w-4 text-indigo-400 print:h-3.5 print:w-3.5" />
                                <span className="text-xs font-bold text-indigo-400 print:text-[10px]">
                                  {holiday.name}
                                </span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-ink-3 print:text-[8px]">Closed</span>
                            )}
                          </td>
                        );
                      }

                      const daySlots = slotsByDate[d.dateISO] ?? [];
                      const matchSlot = daySlots[rowIndex];
                      const periodExams = examsInPeriod(examsByDate.get(d.dateISO) ?? [], slot);
                      const examCards = periodExams.map((exam) => <ExamSlotCard key={exam.uuid} exam={exam} sectionName={exam.classSection.displayName} />);

                      // A free period with a paper in it shows the paper alone.
                      if (periodExams.length > 0 && (!matchSlot || matchSlot.isLeisure)) {
                        return (
                          <td
                            key={d.dateISO}
                            className="p-2 align-middle border-r border-line last:border-r-0 print:p-1"
                          >
                            <div className="flex flex-col gap-1.5">{examCards}</div>
                          </td>
                        );
                      }

                      // If no slot or empty
                      if (!matchSlot || matchSlot.isLeisure) {
                        return (
                          <td
                            key={d.dateISO}
                            className="p-2 text-center align-middle border-r border-line last:border-r-0 print:p-1"
                          >
                            <span className="text-xs text-ink-3/40 font-medium select-none print:text-[10px]">
                              {matchSlot?.isBreak ? 'Break' : '—'}
                            </span>
                          </td>
                        );
                      }

                      // Assigned class period
                      const completed = isPeriodCompleted(d.dateISO, slot.timeEnd);
                      const isPastOrCompleted = d.isPastDay || completed;
                      const isDimmed =
                        selectedStatusFilter !== 'ALL' &&
                        matchSlot.status !== selectedStatusFilter;

                      return (
                        <td
                          key={d.dateISO}
                          className="p-2 align-middle border-r border-line last:border-r-0 print:p-1"
                        >
                          {/* The lesson stays: whether it still runs beside the
                              paper is the teacher's to say from its own cell. */}
                          {examCards.length > 0 && <div className="mb-1.5 flex flex-col gap-1.5">{examCards}</div>}
                          <TimetableSlotCell
                            slot={matchSlot}
                            isPastOrCompleted={isPastOrCompleted}
                            isDimmed={isDimmed}
                            onClick={() => onOpenSlotModal(matchSlot, d)}
                          />
                        </td>
                      );
                    })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
